//  CORE
import { useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React from 'react';

//  DATA
import type { a2aTransfersTypes } from 'app-components/flux/a2aTransfers';
import { a2aTransfersUtils } from 'app-components/flux/a2aTransfers';
import { accountsUtils } from 'app-components/flux/accounts';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';

//  MUI
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import TransferChip from 'components/Chips/TransferChip';
import Dump from 'components/Dump';
// LOCAL
import ThreeDotMenuTransferHistory from 'containers/Settings/ChildPages/TransferHistory/ThreeDotMenuTransferHistory';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';

const useStyles = makeStyles()((theme) => ({
  row: {
    height: 56,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0 8px 0',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    jutifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexBasis: '30%',
    overflow: 'hidden',
  },
  rightGroup: {
    minWidth: 120,
    flexBasis: 112,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  rightStack: {
    display: 'flex',
    flexDirection: 'column',
    jutifyContent: 'flex-start',
    alignItems: 'flex-end',
    overflow: 'hidden',
    minWidth: 66,
  },
  ellipsedText: {
    width: '100%',
  },
  arrow: {
    color: theme.palette.icon.light,
    margin: 'auto, 0',
    // flexBasis: '20%',
    minWidth: 24,
  },
  chipWrapper: {
    minWidth: 90,
    flexShrink: 0,
  },
}));

interface TransferLineProps {
  transfer: a2aTransfersTypes.A2aTransfer;
  index: number;
  id: string;
}

const getAccString = (acc: any) =>
  `${acc?.name || 'Account removed'} ${accountsUtils.formatFinalFour(acc)?.substring(2) || ''}`;
const getInstitutionName = (inst: any) => inst?.name || '';

const dateFormat = (date: string = '') => (date ? DateTime.fromISO(date).toFormat('LLL dd') : '');

const TransferLine = (props: TransferLineProps) => {
  const { classes } = useStyles();

  const { transfer, index, id } = props;

  const sourceFundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, transfer?.sourceFundingAccount?.id),
  );
  const sourceAccount = useAppSelector((state) =>
    fundingAccountSelectors.getAccountByFundingAccountId(state, sourceFundingAccount?.id),
  );

  const destinationFundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, transfer?.destinationFundingAccount?.id),
  );
  const destinationAccount = useAppSelector((state) =>
    fundingAccountSelectors.getAccountByFundingAccountId(state, destinationFundingAccount?.id),
  );

  const sourceInsitution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, sourceAccount?.institutionLoginId),
  );
  const destinationInsitution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, destinationAccount?.institutionLoginId),
  );

  const formatAmount = useFormatAmount();

  const canModifyTransfer =
    a2aTransfersUtils.pendingTransferTypes.includes(transfer?.status || '') &&
    sourceFundingAccount &&
    destinationFundingAccount;

  return (
    <>
      <Dump obj={transfer} />
      <div className={classes.row} id={id}>
        <div className={classes.chipWrapper}>
          <TransferChip status={transfer?.status} id={`status-${index}`} />
        </div>

        <div className={classes.stack}>
          <Typography
            variant='bodyRegular'
            id={`transfer-${index}-source-account`}
            className={classes.ellipsedText}
            noWrap
          >
            {getAccString(sourceAccount)}
          </Typography>

          <Typography
            variant='captionRegular'
            id={`transfer-${index}-sending-institution`}
            color='textSecondary'
            noWrap
            className={classes.ellipsedText}
          >
            {getInstitutionName(sourceInsitution)}
          </Typography>
        </div>

        <ArrowRightAltRoundedIcon className={classes.arrow} />

        <div className={classes.stack}>
          <Typography
            variant='bodyRegular'
            id={`transfer-${index}-destination-account`}
            className={classes.ellipsedText}
            noWrap
          >
            {getAccString(destinationAccount)}
          </Typography>

          <Typography
            variant='captionRegular'
            id={`transfer-${index}-recieving-institution`}
            color='textSecondary'
            noWrap
            className={classes.ellipsedText}
          >
            {getInstitutionName(destinationInsitution)}
          </Typography>
        </div>

        <div className={classes.rightGroup}>
          <div className={classes.rightStack}>
            <Typography variant='bodyRegular' id={`transfer-${index}-amount`} noWrap align='right'>
              {formatAmount(transfer.amount, 'USD', '0,00.00', 'never')}
            </Typography>

            <Typography
              variant='captionRegular'
              id={`transfer-${index}-expected-date`}
              color='textSecondary'
              noWrap
              align='right'
            >
              {a2aTransfersUtils.completedTransferTypes.includes(transfer?.status || '')
                ? dateFormat(
                    transfer?.execution?.completedOn ||
                      transfer?.execution?.cancelledOn ||
                      transfer?.execution?.failedOn ||
                      '',
                  )
                : `Est. ${dateFormat(transfer?.schedule?.completeOn)}`}
            </Typography>
          </div>
          {canModifyTransfer ? <ThreeDotMenuTransferHistory id={`${id}-menu`} transfer={transfer} /> : null}
        </div>
      </div>
      <Divider sx={{ margin: '8px 0 8px 0' }} />
    </>
  );
};

TransferLine.displayName = 'TransferLine';

export default TransferLine;
